import { SelectChangeEvent } from '@mui/material';
import _ from 'lodash';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
    EquipmentTypeDto,
    MaterialDto,
    MaterialLineItemDto,
    MaterialPriceCostDto,
    MaterialPriceDto,
    QuoteDto,
    RateToggle,
    RegionDto,
    SiteDto,
    SiteHaulingLineItemDto,
} from '../../../../../dtos';
import { countries, emptyGuid, QuoteType, UnitOfMeasure, unitsOfMeasure } from '../../../../../util';
import { useGetRegionQuery } from '../../../../../store/generated/generatedApi';
import {
    IFormFieldValidationConfig,
    IFormProps,
    isGreaterThanOrEqualTo,
    isNotBlank,
    isShorterThanMaxLength,
    runFieldValidation,
    runFormValidation,
} from '../../../../CoreLib/library';
import { BilledToType } from '../../../../../dtos/enums/BilledToType';

const isValidUnitOfMeasure = (value: string): value is UnitOfMeasure => {
    return unitsOfMeasure.includes(value);
};

export interface IMaterialLineItemFormValues {
    cost?: number;
    equipmentTypeId: string;
    isActive: boolean;
    isBilledTo: BilledToType;
    billedTo?: string;
    isMaterialOnly: boolean;
    materialId: string;
    materialPriceId?: string;
    materialPriceCostId?: string;
    newHaulingLineItemDriverRate?: number;
    newHaulingLineItemRate?: number;
    newHaulingLineItemBrokerRate?: number;
    newHaulingLineItemMinimumFlatRate?: number;
    newHaulingLineItemDriverMinimumFlatRate?: number;
    newHaulingLineItemBrokerMinimumFlatRate?: number;
    newHaulingLineItemZone?: string;
    newHaulingLineItemDriverMemo?: string;
    priceCode: string;
    quantity?: number;
    quoteId: string;
    lineItemMemo?: string;
    driverMemo?: string;
    rate?: number;
    siteId: string;
    total?: number;
    unitOfMeasure: string;
    rateToggle: RateToggle;
    associatedLineItemId?: string;
    customerJobNumber?: string;
    poNumber?: string;
    isCustomPoNumber: boolean;
    isCustomerJobNumberCustom: boolean;
    isCustomDriverMemo: boolean;
    haulFromAddressLine1?: string;
    haulFromAddressLine2?: string;
    haulFromCity?: string;
    haulFromState?: string;
    haulFromZipCode?: string;
    haulFromCountry?: string;
    haulFromDirections?: string;
    haulFromSiteName?: string;
    haulFromRegion?: RegionDto;
    isMaterialTaxable?: boolean;
}

export const DEFAULT_MATERIAL_LINE_ITEM: MaterialLineItemDto = {
    id: emptyGuid,
    quoteId: '',
    siteId: '',
    equipmentTypeId: '',
    materialId: '',
    materialPriceId: '',
    materialPriceCostId: '',
    lineItemMemo: '',
    driverMemo: '',
    rate: 0,
    quantity: 0,
    isBilledTo: BilledToType.TriAx,
    billedTo: '',
    isMaterialOnly: false,
    priceCode: '',
    cost: 0,
    newHaulingLineItemRate: 0,
    newHaulingLineItemDriverRate: 0,
    newHaulingLineItemBrokerRate: 0,
    newHaulingLineItemMinimumFlatRate: undefined,
    newHaulingLineItemDriverMinimumFlatRate: undefined,
    newHaulingLineItemBrokerMinimumFlatRate: undefined,
    newHaulingLineItemZone: '',
    newHaulingLineItemDriverMemo: '',
    unitOfMeasure: 'Tons',
    isActive: true,
    createdOn: new Date(),
    rateToggle: RateToggle.Standard,
    customerJobNumber: '',
    poNumber: '',
    isCustomPoNumber: false,
    isCustomerJobNumberCustom: false,
    isCustomDriverMemo: false,
};

export function useMaterialLineItemForm(props: IFormProps<MaterialLineItemDto>, quote: QuoteDto | undefined) {
    const { save, cancel, initValues } = props;
    const initIsMaterialTaxable = (initValues as IMaterialLineItemFormValues | undefined)?.isMaterialTaxable;
    const [formQuote, setFormQuote] = useState<QuoteDto | undefined>(initValues?.quote);
    const [formEquipmentType, setFormEquipmentType] = useState<EquipmentTypeDto | undefined>(initValues?.equipmentType);
    const [formSite, setFormSite] = useState<SiteDto | undefined>(initValues?.site);
    const [formMaterialPrice, setFormMaterialPrice] = useState<MaterialPriceDto | undefined>(initValues?.materialPrice);
    const [formMaterialPriceCost, setFormMaterialPriceCost] = useState<MaterialPriceCostDto | undefined>(initValues?.materialPriceCost);
    const [formMaterial, setFormMaterial] = useState<MaterialDto | undefined>(initValues?.material);
    const [formUnitOfMeasure, setFormUnitOfMeasure] = useState<UnitOfMeasure>((initValues?.unitOfMeasure as UnitOfMeasure) ?? 'Tons');
    const [formLineItemMemo, setFormLineItemMemo] = useState<string | undefined>(initValues?.lineItemMemo);
    const [formDriverMemo, setFormDriverMemo] = useState<string | undefined>(initValues?.driverMemo);
    const [formRate, setFormRate] = useState<number | undefined>(initValues?.rate);
    const [formQuantity, setFormQuantity] = useState<number | undefined>(initValues?.quantity);
    const [formIsBilledTo, setFormIsBilledTo] = useState<BilledToType | undefined>(initValues?.isBilledTo ?? BilledToType.TriAx);
    const [formBilledTo, setFormBilledTo] = useState<string | undefined>(initValues?.billedTo ?? '');
    const [formIsMaterialOnly, setFormIsMaterialOnly] = useState<boolean>(initValues?.isMaterialOnly ?? false);
    const [formPriceCode, setFormPriceCode] = useState<string>(initValues?.priceCode ?? '');
    const [formCost, setFormCost] = useState<number | undefined>(initValues?.cost);
    const [formNewHaulingLineItemRate, setFormNewHaulingLineItemRate] = useState<number | undefined>();
    const [formNewHaulingLineItemDriverRate, setFormNewHaulingLineItemDriverRate] = useState<number | undefined>();
    const [formNewHaulingLineItemBrokerRate, setFormNewHaulingLineItemBrokerRate] = useState<number | undefined>();
    const [formNewHaulingLineItemMinimumFlatRate, setFormHaulingLineItemMinimumFlatRateChange] = useState<number | undefined>();
    const [formNewHaulingLineItemDriverMinimumFlatRate, setFormHaulingLineItemDriverMinimumFlatRateChange] = useState<number | undefined>();
    const [formNewHaulingLineItemBrokerMinimumFlatRate, setFormHaulingLineItemBrokerMinimumFlatRateChange] = useState<number | undefined>();
    const [formNewHaulingLineItemZone, setFormHaulingLineItemZoneChange] = useState<string | undefined>();
    const [formRateToggle, setFormRateToggle] = useState<RateToggle>(initValues?.rateToggle ?? RateToggle.Standard);
    const [formIsActive, setFormIsActive] = useState<boolean>(initValues?.isActive ?? true);
    const [formCustomerJobNumber, setFormCustomerJobNumber] = useState<string | undefined>(
        initValues?.isCustomerJobNumberCustom ? (initValues?.customerJobNumber === '' ? quote?.customerJobNumber ?? '' : initValues?.customerJobNumber ?? quote?.customerJobNumber ?? '') : quote?.customerJobNumber ?? ''
    );
    const [formPoNumber, setFormPoNumber] = useState<string | undefined>(
        initValues?.isCustomPoNumber ? (initValues?.poNumber === '' ? quote?.poNumber ?? '' : initValues?.poNumber ?? quote?.poNumber ?? '') : quote?.poNumber ?? ''
    );
    const [formQuotePoNumber, setFormQuotePoNumber] = useState<string | undefined>(quote?.poNumber);
    const [formQuoteCustomerJobNumber, setFormQuoteCustomerJobNumber] = useState<string | undefined>(quote?.customerJobNumber);
    const [formIsCustomPoNumber, setFormIsCustomPoNumber] = useState<boolean>(initValues?.isCustomPoNumber ?? false);
    const [formIsCustomerJobNumberCustom, setFormIsCustomerJobNumberCustom] = useState<boolean>(initValues?.isCustomerJobNumberCustom ?? false);
    const [formIsCustomDriverMemo, setFormIsCustomDriverMemo] = useState<boolean>(initValues?.isCustomDriverMemo ?? false);
    const [formAddressLine1, setFormAddressLine1] = useState(initValues?.haulFromAddressLine1 ?? '');
    const [formAddressLine2, setFormAddressLine2] = useState(initValues?.haulFromAddressLine2 ?? '');
    const [formCountry, setFormCountry] = useState(initValues?.haulFromCountry ?? countries[2]);
    const [formDirections, setFormDirections] = useState(initValues?.haulFromDirections ?? '');
    const [formCity, setFormCity] = useState(initValues?.haulFromCity ?? '');
    const [formState, setFormState] = useState(initValues?.haulFromState ?? 'IN');
    const [formZipCode, setFormZipCode] = useState(initValues?.haulFromZipCode ?? '');
    const [formRegion, setFormRegion] = useState<RegionDto | undefined>(initValues?.haulFromRegion);
    const [formIsMaterialTaxable, setFormIsMaterialTaxable] = useState<boolean>(
        initIsMaterialTaxable ?? (quote?.isTaxable === true)
    );

    const { data: regions } = useGetRegionQuery({ searchText: '', sorting: ['NAME+'], page: 0, pageSize: 100000, includeInactive: false });

    /** Site name always matches the pricing site for pickup (haul-from). */
    const formSiteName = useMemo(() => formSite?.name ?? '', [formSite?.name]);

    const [currentSiteHaulingLineItem, setCurrentSiteHaulingLineItem] = useState<SiteHaulingLineItemDto | undefined>(initValues?.associatedLineItem);

    const isNewMaterialLineItem = !initValues?.id || initValues.id === emptyGuid;

    useEffect(() => {
        setFormQuotePoNumber(quote?.poNumber);
        setFormQuoteCustomerJobNumber(quote?.customerJobNumber);
        setFormPoNumber(initValues?.isCustomPoNumber ? (initValues?.poNumber === '' ? quote?.poNumber ?? '' : initValues?.poNumber ?? quote?.poNumber ?? '') : quote?.poNumber ?? '');
        setFormCustomerJobNumber(initValues?.isCustomerJobNumberCustom ? (initValues?.customerJobNumber === '' ? quote?.customerJobNumber ?? '' : initValues?.customerJobNumber ?? quote?.customerJobNumber ?? '') : quote?.customerJobNumber ?? '');
    }, [quote, initValues?.isCustomPoNumber, initValues?.poNumber, initValues?.customerJobNumber, initValues?.isCustomerJobNumberCustom, initValues?.driverMemo, initValues?.associatedLineItem?.driverMemo, initValues?.isCustomDriverMemo]);

    useEffect(() => {
        setFormAddressLine1(initValues?.haulFromAddressLine1 ?? '');
        setFormAddressLine2(initValues?.haulFromAddressLine2 ?? '');
        setFormCountry(initValues?.haulFromCountry ?? countries[2]);
        setFormDirections(initValues?.haulFromDirections ?? '');
        setFormCity(initValues?.haulFromCity ?? '');
        setFormState(initValues?.haulFromState ?? 'IN');
        setFormZipCode(initValues?.haulFromZipCode ?? '');
        setFormRegion(initValues?.haulFromRegion);
        // Primitives + region id (not haulFromRegion object) so parent updates sync without reference-churn resets.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- haulFromRegion?.id tracks region changes; full RegionDto in deps re-runs when parent passes new object identity
    }, [
        initValues?.id,
        initValues?.haulFromAddressLine1,
        initValues?.haulFromAddressLine2,
        initValues?.haulFromCountry,
        initValues?.haulFromDirections,
        initValues?.haulFromCity,
        initValues?.haulFromState,
        initValues?.haulFromZipCode,
        initValues?.haulFromRegion?.id,
    ]);

    useEffect(() => {
        setFormSite(initValues?.site);
        // initValues?.site?.id tracks site changes; full SiteDto in deps re-runs when parent passes new object identity
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initValues?.id, initValues?.site?.id]);

    useEffect(() => {
        if (quote?.isTaxable === false) {
            setFormIsMaterialTaxable(false);
        }
    }, [quote?.isTaxable]);

    const isNewHaulingLineItem = useMemo(() => {
        return currentSiteHaulingLineItem === undefined;
    }, [currentSiteHaulingLineItem]);

    useEffect(() => {
        if (!formIsCustomDriverMemo) {
            setFormDriverMemo(currentSiteHaulingLineItem?.driverMemo);
        }
    }, [formIsCustomDriverMemo, currentSiteHaulingLineItem?.driverMemo]);

    const isFobQuote = useMemo(() => {
        return formQuote?.type === QuoteType.FOB;
    }, [formQuote?.type]);

    const formTotal = useMemo(() => {
        return (formQuantity ?? 0) * (formRate ?? 0);
    }, [formQuantity, formRate]);

    const [fieldErrors, setFieldErrors] = useState<Map<keyof IMaterialLineItemFormValues, string>>(
        new Map([
            ['quoteId', ''],
            ['equipmentTypeId', ''],
            ['siteId', ''],
            ['rate', ''],
            ['lineItemMemo', ''],
            ['isBilledTo', ''],
            ['billedTo', ''],
            ['unitOfMeasure', ''],
            ['materialPriceId', ''],
            ['materialId', ''],
            ['quantity', ''],
            ['total', ''],
            ['newHaulingLineItemRate', ''],
            ['newHaulingLineItemDriverRate', ''],
            ['newHaulingLineItemBrokerRate', ''],
            ['newHaulingLineItemMinimumFlatRate', ''],
            ['newHaulingLineItemDriverMinimumFlatRate', ''],
            ['newHaulingLineItemBrokerMinimumFlatRate', ''],
            ['priceCode', ''],
            ['cost', ''],
            ['customerJobNumber', ''],
            ['poNumber', ''],
        ])
    );

    const doesNotRequireNewHaulingLineItem = useCallback(() => {
        if (isFobQuote || formIsMaterialOnly) {
            return true;
        }
        return currentSiteHaulingLineItem !== undefined && currentSiteHaulingLineItem?.id !== '';
    }, [currentSiteHaulingLineItem, isFobQuote, formIsMaterialOnly]);

    const formFieldValidators = useMemo(
        () =>
            new Map<keyof IMaterialLineItemFormValues, IFormFieldValidationConfig>([
                [
                    'quoteId',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Quote',
                    },
                ],
                [
                    'equipmentTypeId',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Equipment Type',
                    },
                ],
                [
                    'siteId',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Site',
                    },
                ],
                [
                    'materialId',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Material',
                    },
                ],
                [
                    'materialPriceId',
                    {
                        validators: formIsBilledTo === BilledToType.Customer || formIsBilledTo === BilledToType.Custom ? [] : [isNotBlank],
                        errorMessageEntityName: 'Material Price',
                    },
                ],
                [
                    'total',
                    {
                        validators: [isNotBlank, isGreaterThanOrEqualTo(0)],
                        errorMessageEntityName: 'Total',
                    },
                ],
                [
                    'rate',
                    {
                        validators: [isNotBlank, isGreaterThanOrEqualTo(0)],
                        errorMessageEntityName: 'Rate',
                    },
                ],
                [
                    'quantity',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Quantity',
                    },
                ],
                [
                    'cost',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Cost',
                    },
                ],
                [
                    'newHaulingLineItemRate',
                    {
                        validators: !doesNotRequireNewHaulingLineItem() && formRateToggle !== RateToggle.Flat ? [isNotBlank] : [],
                        errorMessageEntityName: 'Hauling Rate',
                    },
                ],
                [
                    'newHaulingLineItemDriverRate',
                    {
                        validators: !doesNotRequireNewHaulingLineItem() && formRateToggle !== RateToggle.Flat ? [isNotBlank] : [],
                        errorMessageEntityName: 'Driver Rate',
                    },
                ],
                [
                    'newHaulingLineItemBrokerRate',
                    {
                        validators: !doesNotRequireNewHaulingLineItem() && formRateToggle !== RateToggle.Flat ? [isNotBlank] : [],
                        errorMessageEntityName: 'Broker Rate',
                    },
                ],
                [
                    'newHaulingLineItemMinimumFlatRate',
                    {
                        validators: !doesNotRequireNewHaulingLineItem() && formRateToggle !== RateToggle.Standard ? [isNotBlank] : [],
                        errorMessageEntityName: 'Minimum Flat Rate',
                    },
                ],
                [
                    'newHaulingLineItemDriverMinimumFlatRate',
                    {
                        validators: !doesNotRequireNewHaulingLineItem() && formRateToggle !== RateToggle.Standard ? [isNotBlank] : [],
                        errorMessageEntityName: 'Driver Minimum Flat Rate',
                    },
                ],
                [
                    'newHaulingLineItemBrokerMinimumFlatRate',
                    {
                        validators: !doesNotRequireNewHaulingLineItem() && formRateToggle !== RateToggle.Standard ? [isNotBlank] : [],
                        errorMessageEntityName: 'Broker Minimum Flat Rate',
                    },
                ],
                [
                    'unitOfMeasure',
                    {
                        validators: [isNotBlank, isShorterThanMaxLength(20)],
                        errorMessageEntityName: 'Unit Of Measure',
                    },
                ],
                [
                    'priceCode',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Price Code',
                    },
                ],
                [
                    'isBilledTo',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Billed To',
                    },
                ],
                [
                    'billedTo',
                    {
                        validators: formIsBilledTo === BilledToType.Custom ? [isNotBlank] : [],
                        errorMessageEntityName: 'Billed To',
                    },
                ],
                [
                    'poNumber',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'PO Number',
                    },
                ]
            ]),
        [doesNotRequireNewHaulingLineItem, formIsBilledTo, formRateToggle]
    );
    const getCurrentFormValues = useCallback((): IMaterialLineItemFormValues => {
        return {
            quoteId: formQuote?.id ?? '',
            siteId: formSite?.id ?? '',
            equipmentTypeId: formEquipmentType?.id ?? '',
            materialPriceId: formMaterialPrice?.id,
            materialPriceCostId: formMaterialPriceCost?.id,
            materialId: formMaterial?.id ?? '',
            lineItemMemo: formLineItemMemo ?? '',
            driverMemo: formDriverMemo ?? '',
            rate: formRate,
            quantity: formQuantity ?? 1,
            isBilledTo: formIsBilledTo ?? BilledToType.TriAx,
            billedTo: formBilledTo ?? '',
            isMaterialOnly: formIsMaterialOnly ?? false,
            priceCode: formPriceCode ?? '',
            cost: formCost,
            total: formTotal,
            newHaulingLineItemRate: formNewHaulingLineItemRate,
            newHaulingLineItemDriverRate: formNewHaulingLineItemDriverRate,
            newHaulingLineItemBrokerRate: formNewHaulingLineItemBrokerRate,
            newHaulingLineItemMinimumFlatRate: formNewHaulingLineItemMinimumFlatRate,
            newHaulingLineItemDriverMinimumFlatRate: formNewHaulingLineItemDriverMinimumFlatRate,
            newHaulingLineItemBrokerMinimumFlatRate: formNewHaulingLineItemBrokerMinimumFlatRate,
            newHaulingLineItemZone: formNewHaulingLineItemZone,
            newHaulingLineItemDriverMemo: (isNewHaulingLineItem && !formIsCustomDriverMemo) ? formDriverMemo : "",
            unitOfMeasure: formUnitOfMeasure ?? '',
            isActive: formIsActive,
            rateToggle: formRateToggle,
            associatedLineItemId: currentSiteHaulingLineItem?.id,
            customerJobNumber: formCustomerJobNumber ?? '',
            poNumber: formPoNumber ?? '',
            isCustomPoNumber: formIsCustomPoNumber,
            isCustomerJobNumberCustom: formIsCustomerJobNumberCustom,
            isCustomDriverMemo: formIsCustomDriverMemo,
            haulFromAddressLine1: formAddressLine1,
            haulFromAddressLine2: formAddressLine2,
            haulFromCity: formCity,
            haulFromState: formState,
            haulFromZipCode: formZipCode,
            haulFromCountry: formCountry,
            haulFromDirections: formDirections,
            haulFromSiteName: formSite?.name ?? '',
            haulFromRegion: formRegion,
            isMaterialTaxable: quote?.isTaxable ? formIsMaterialTaxable : false,
        };
    }, [formQuote?.id, formSite?.id, formSite?.name, formEquipmentType?.id, formMaterialPrice?.id, formMaterialPriceCost?.id, formMaterial?.id, formLineItemMemo, formDriverMemo, formRate, formQuantity, formIsBilledTo, formBilledTo, formIsMaterialOnly, formPriceCode, formCost, formTotal, formNewHaulingLineItemRate, formNewHaulingLineItemDriverRate, formNewHaulingLineItemBrokerRate, formNewHaulingLineItemMinimumFlatRate, formNewHaulingLineItemDriverMinimumFlatRate, formNewHaulingLineItemBrokerMinimumFlatRate, formNewHaulingLineItemZone, isNewHaulingLineItem, formUnitOfMeasure, formIsActive, formRateToggle, currentSiteHaulingLineItem?.id, formCustomerJobNumber, formPoNumber, formIsCustomPoNumber, formIsCustomerJobNumberCustom, formIsCustomDriverMemo, formIsMaterialTaxable, quote?.isTaxable, formAddressLine1, formAddressLine2, formCity, formState, formZipCode, formCountry, formDirections, formRegion]);

    const validateForm = useCallback(() => {
        const formValues = getCurrentFormValues();
        const validationResult = runFormValidation<Partial<IMaterialLineItemFormValues>>(formValues, formFieldValidators);
        setFieldErrors(validationResult.errorMessages);
        return validationResult.isValid;
    }, [formFieldValidators, getCurrentFormValues]);

    const validateField = useCallback(
        (fieldName: keyof IMaterialLineItemFormValues) => {
            const validationConfig = formFieldValidators.get(fieldName);
            if (validationConfig) {
                const formValues = getCurrentFormValues();
                const fieldValue = formValues[fieldName];
                const { errorMessage } = runFieldValidation(fieldValue, validationConfig);

                if (errorMessage !== fieldErrors.get(fieldName)) {
                    const updatedFieldErrors = _.cloneDeep(fieldErrors);
                    updatedFieldErrors.set(fieldName, errorMessage);
                    setFieldErrors(updatedFieldErrors);
                }
            }
        },
        [fieldErrors, formFieldValidators, getCurrentFormValues]
    );

    const isFormDirty = useCallback(() => {
        var isDirty = formIsActive !== initValues?.isActive;
        isDirty = isDirty || formCost !== initValues?.cost;
        isDirty = isDirty || formEquipmentType?.id !== initValues?.equipmentType?.id;
        isDirty = isDirty || formIsBilledTo !== initValues?.isBilledTo;
        isDirty = isDirty || formBilledTo !== initValues?.billedTo;
        isDirty = isDirty || formMaterialPrice?.id !== initValues?.materialPrice?.id;
        isDirty = isDirty || formMaterialPriceCost?.id !== initValues?.materialPriceCost?.id;
        isDirty = isDirty || formPriceCode !== initValues?.priceCode;
        isDirty = isDirty || formQuantity !== initValues?.quantity;
        isDirty = isDirty || formQuote?.id !== initValues?.quote?.id;
        isDirty = isDirty || formLineItemMemo !== initValues?.lineItemMemo;
        isDirty = isDirty || formDriverMemo !== initValues?.driverMemo;
        isDirty = isDirty || formRate !== initValues?.rate;
        isDirty = isDirty || formSite?.id !== initValues?.site?.id;
        isDirty = isDirty || formUnitOfMeasure !== initValues?.unitOfMeasure;
        isDirty = isDirty || formRateToggle !== initValues?.rateToggle;
        isDirty = isDirty || formIsMaterialOnly !== initValues?.isMaterialOnly;
        isDirty = isDirty || currentSiteHaulingLineItem?.id !== initValues?.associatedLineItem?.id;
        isDirty = isDirty || formCustomerJobNumber !== initValues?.customerJobNumber;
        isDirty = isDirty || formPoNumber !== initValues?.poNumber;
        isDirty = isDirty || formIsCustomPoNumber !== initValues?.isCustomPoNumber;
        isDirty = isDirty || formIsCustomerJobNumberCustom !== initValues?.isCustomerJobNumberCustom;
        isDirty = isDirty || formIsCustomDriverMemo !== initValues?.isCustomDriverMemo;
        isDirty = isDirty || formAddressLine1 !== initValues?.haulFromAddressLine1;
        isDirty = isDirty || formAddressLine2 !== initValues?.haulFromAddressLine2;
        isDirty = isDirty || formCity !== initValues?.haulFromCity;
        isDirty = isDirty || formState !== initValues?.haulFromState;
        isDirty = isDirty || formZipCode !== initValues?.haulFromZipCode;
        isDirty = isDirty || formCountry !== initValues?.haulFromCountry;
        isDirty = isDirty || formDirections !== initValues?.haulFromDirections;
        isDirty = isDirty || (initValues?.haulFromSiteName ?? '') !== (formSite?.name ?? '');
        isDirty = isDirty || formRegion !== initValues?.haulFromRegion;
        isDirty = isDirty || formIsMaterialTaxable !== initIsMaterialTaxable;

        return isDirty;
    }, [
        formIsActive,
        initValues?.isActive,
        initValues?.cost,
        initValues?.equipmentType?.id,
        initValues?.isBilledTo,
        initValues?.billedTo,
        initValues?.materialPrice?.id,
        initValues?.materialPriceCost?.id,
        initValues?.priceCode,
        initValues?.quantity,
        initValues?.quote?.id,
        initValues?.lineItemMemo,
        initValues?.driverMemo,
        initValues?.rate,
        initValues?.site?.id,
        initValues?.unitOfMeasure,
        initValues?.rateToggle,
        initValues?.isMaterialOnly,
        initValues?.associatedLineItem?.id,
        initValues?.customerJobNumber,
        initValues?.poNumber,
        initValues?.isCustomPoNumber,
        initValues?.isCustomerJobNumberCustom,
        initValues?.isCustomDriverMemo,
        initValues?.haulFromAddressLine1,
        initValues?.haulFromAddressLine2,
        initValues?.haulFromCity,
        initValues?.haulFromState,
        initValues?.haulFromZipCode,
        initValues?.haulFromCountry,
        initValues?.haulFromDirections,
        initValues?.haulFromSiteName,
        initValues?.haulFromRegion,
        formAddressLine1,
        formAddressLine2,
        formCity,
        formState,
        formZipCode,
        formCountry,
        formDirections,
        formSite?.name,
        formRegion,
        initIsMaterialTaxable,
        formCost,
        formEquipmentType?.id,
        formIsBilledTo,
        formBilledTo,
        formMaterialPrice?.id,
        formMaterialPriceCost?.id,
        formPriceCode,
        formQuantity,
        formQuote?.id,
        formLineItemMemo,
        formDriverMemo,
        formRate,
        formSite?.id,
        formUnitOfMeasure,
        formRateToggle,
        formIsMaterialOnly,
        currentSiteHaulingLineItem?.id,
        formCustomerJobNumber,
        formPoNumber,
        formIsCustomPoNumber,
        formIsCustomerJobNumberCustom,
        formIsCustomDriverMemo,
        formIsMaterialTaxable
    ]);

    const handleFormAddressChange = useCallback((address) => {
        setFormAddressLine1(address.addressLine1 ?? '');
        setFormAddressLine2(address.addressLine2 ?? '');
        setFormCity(address.city ?? '');
        setFormState(address.state ?? '');
        setFormZipCode(address.zipCode ?? '');
        setFormCountry(address.country ?? countries[2]);
        setFormDirections(address.directions ?? '');
    }, []);

    const handleFormRegionChange = useCallback((region: RegionDto | undefined) => {
        setFormRegion(region);
    }, []);

    /** Populates haul-from (pickup) address fields from the pricing site. */
    const applyHaulFromFromSite = useCallback(
        (site?: SiteDto) => {
            handleFormAddressChange({
                addressLine1: site?.addressLine1,
                addressLine2: site?.addressLine2,
                city: site?.city,
                state: site?.state,
                zipCode: site?.zipCode,
                country: site?.country,
                directions: site?.directions,
            });
            handleFormRegionChange(site?.region);
        },
        [handleFormAddressChange, handleFormRegionChange]
    );

    const handleCostChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        if (!isNaN(parseFloat(event.target.value))) {
            setFormCost(parseFloat(event.target.value));
        } else {
            setFormCost(undefined);
        }
    }, []);

    const handleLineItemMemoChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormLineItemMemo(event.target.value ?? '');
    }, []);

    const handleDriverMemoChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormDriverMemo(event.target.value ?? '');
    }, []);

    const handleIsCustomDriverMemoChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormIsCustomDriverMemo(event.target.checked);
    }, []);

    const handleCustomerJobNumberChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormCustomerJobNumber(event.target.value ?? '');
    }, []);

    const handlePoNumberChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormPoNumber(event.target.value ?? '');
    }, []);

    const handleEquipmentTypeChange = useCallback((equipmentType?: EquipmentTypeDto) => {
        setFormEquipmentType(equipmentType);
    }, []);

    const handleMaterialChange = useCallback((material?: MaterialDto) => {
        setFormMaterial(material);
        setFormMaterialPrice(undefined);
    }, []);

    const clearHaulingLineItemFields = useCallback(() => {
        setCurrentSiteHaulingLineItem(undefined);
        setFormNewHaulingLineItemBrokerRate(undefined);
        setFormNewHaulingLineItemDriverRate(undefined);
        setFormNewHaulingLineItemRate(undefined);
        setFormHaulingLineItemMinimumFlatRateChange(undefined);
        setFormHaulingLineItemDriverMinimumFlatRateChange(undefined);
        setFormHaulingLineItemBrokerMinimumFlatRateChange(undefined);
        setFormHaulingLineItemZoneChange(undefined);
        setFormRateToggle(RateToggle.Standard);
    }, []);

    const handleMaterialPriceChange = useCallback(
        (materialPrice?: MaterialPriceDto) => {
            clearHaulingLineItemFields();
            setFormMaterialPrice(materialPrice);
            setFormMaterialPriceCost(materialPrice?.currentMaterialPriceCost);
            setFormSite(materialPrice?.site);
            setFormUnitOfMeasure(materialPrice?.currentUnitOfMeasure as UnitOfMeasure);
            setFormCost(materialPrice?.currentCost);
            setFormQuantity(1);
            switch (formPriceCode) {
                case 'A':
                    setFormRate(materialPrice?.currentPriceA);
                    break;
                case 'B':
                    setFormRate(materialPrice?.currentPriceB);
                    break;
                case 'C':
                    setFormRate(materialPrice?.currentPriceC);
                    break;
                case 'D':
                    setFormRate(materialPrice?.currentPriceD);
                    break;
                case 'E':
                    setFormRate(materialPrice?.currentPriceE);
                    break;
                default:
                    setFormPriceCode('C');
                    setFormRate(materialPrice?.currentPriceC);
                    break;
            }
            applyHaulFromFromSite(materialPrice?.site);
        },
        [applyHaulFromFromSite, clearHaulingLineItemFields, formPriceCode]
    );

    const handleIsBilledToChange = useCallback((value?: BilledToType) => {
        setFormIsBilledTo(value);

        setFormMaterialPrice(undefined);
        setFormMaterialPriceCost(undefined);
        setFormPriceCode('Custom');
        setFormCost(0);
        setFormRate(0);
        setFormQuantity(1);
        setFormUnitOfMeasure('Tons');
    }, []);

    const handleBilledToChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormBilledTo(event.target.value ?? '');
    }, []);

    const handleIsMaterialOnlyChange = useCallback(
        (_: ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
            setFormIsMaterialOnly(isChecked);
            clearHaulingLineItemFields();
        },
        [clearHaulingLineItemFields]
    );

    const handleHaulingDriverRateChange = useCallback((value?: number) => {
        setFormNewHaulingLineItemDriverRate(value);
    }, []);

    const handleHaulingHaulingRateChange = useCallback((value?: number) => {
        setFormNewHaulingLineItemRate(value);
    }, []);

    const handleHaulingBrokerRateChange = useCallback((value?: number) => {
        setFormNewHaulingLineItemBrokerRate(value);
    }, []);

    const handleHaulingMinimumFlatRateChange = useCallback((value?: number) => {
        setFormHaulingLineItemMinimumFlatRateChange(value);
    }, []);

    const handleHaulingDriverMinimumFlatRateChange = useCallback((value?: number) => {
        setFormHaulingLineItemDriverMinimumFlatRateChange(value);
    }, []);

    const handleHaulingBrokerMinimumFlatRateChange = useCallback((value?: number) => {
        setFormHaulingLineItemBrokerMinimumFlatRateChange(value);
    }, []);

    const handleHaulingZoneChange = useCallback((value?: string) => {
        setFormHaulingLineItemZoneChange(value);
    }, []);

    const handleRateToggleChange = useCallback((rateToggle: RateToggle) => {
        setFormRateToggle(rateToggle);
    }, []);

    const getCurrentRate = useCallback(
        (priceCode: string) => {
            switch (priceCode) {
                case 'A':
                    return formMaterialPrice?.currentPriceA;
                case 'B':
                    return formMaterialPrice?.currentPriceB;
                case 'C':
                    return formMaterialPrice?.currentPriceC;
                case 'D':
                    return formMaterialPrice?.currentPriceD;
                case 'E':
                    return formMaterialPrice?.currentPriceE;
                default:
                    return 0;
            }
        },
        [
            formMaterialPrice?.currentPriceA,
            formMaterialPrice?.currentPriceB,
            formMaterialPrice?.currentPriceC,
            formMaterialPrice?.currentPriceD,
            formMaterialPrice?.currentPriceE,
        ]
    );

    const handlePriceCodeChange = useCallback(
        (event: SelectChangeEvent<string>) => {
            const priceCode = event.target.value;
            setFormPriceCode(priceCode);
            if (priceCode !== 'Custom') {
                if (priceCode === '') {
                    setFormCost(undefined);
                } else {
                    setFormCost(formMaterialPrice?.currentCost);
                }
            }

            setFormRate(getCurrentRate(priceCode));
        },
        [formMaterialPrice?.currentCost, getCurrentRate]
    );

    const handleQuantityChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormQuantity(isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber);
    }, []);

    const handleQuoteChange = useCallback((quote: QuoteDto) => {
        setFormQuote(quote);
    }, []);

    const handleRateChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormRate(isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber);
    }, []);

    const handleSiteChange = useCallback((site?: SiteDto) => {
        setFormSite(site);
        setFormMaterialPrice(undefined);
        setFormMaterialPriceCost(undefined);
        setFormRate(0);
        applyHaulFromFromSite(site);
    }, [applyHaulFromFromSite]);

    const handleUnitOfMeasureChange = useCallback((value?: string) => {
        if (isValidUnitOfMeasure(value ?? '')) {
            setFormUnitOfMeasure(value as UnitOfMeasure);
        } else {
            console.error('Attempted to set unit of measure to invalid value.');
        }
    }, []);

    const handleIsActiveChange = useCallback((_: ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
        setFormIsActive(isChecked);
    }, []);

    const handleSave = useCallback(() => {
        const isFormValid = validateForm();
        if (isFormValid) {
            const formValues = getCurrentFormValues();
            const updatedUserRecord = {
                ...(initValues ?? DEFAULT_MATERIAL_LINE_ITEM),
                ...formValues,
            };
            save(updatedUserRecord);
        }
    }, [getCurrentFormValues, initValues, save, validateForm]);

    const handleCancel = useCallback(() => {
        cancel();
    }, [cancel]);

    const handleIsCustomPoNumberChange = useCallback((value: boolean) => {
        setFormIsCustomPoNumber(value);
        if (!value) {
            setFormPoNumber(formQuotePoNumber);
        }
    }, [formQuotePoNumber, setFormPoNumber]);

    const handleIsCustomerJobNumberCustomChange = useCallback((value: boolean) => {
        setFormIsCustomerJobNumberCustom(value);
        if (!value) {
            setFormCustomerJobNumber(formQuoteCustomerJobNumber);
        }
    }, [formQuoteCustomerJobNumber, setFormCustomerJobNumber]);

    const handleIsMaterialTaxableChange = useCallback((_: ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
        setFormIsMaterialTaxable(isChecked);
    }, []);

    return {
        isFormDirty,
        handleSave,
        handleCancel,
        fieldErrors,
        handleCostChange,
        handleEquipmentTypeChange,
        handleHaulingDriverRateChange,
        handleHaulingHaulingRateChange,
        handleHaulingBrokerRateChange,
        handleIsActiveChange,
        handleIsBilledToChange,
        handleBilledToChange,
        handleIsMaterialOnlyChange,
        handleMaterialChange,
        handleMaterialPriceChange,
        handlePriceCodeChange,
        handleQuantityChange,
        handleQuoteChange,
        handleRateChange,
        handleSiteChange,
        handleUnitOfMeasureChange,
        handleLineItemMemoChange,
        handleDriverMemoChange,
        handleIsCustomDriverMemoChange,
        handleCustomerJobNumberChange,
        handlePoNumberChange,
        handleHaulingMinimumFlatRateChange,
        handleHaulingDriverMinimumFlatRateChange,
        handleHaulingBrokerMinimumFlatRateChange,
        handleHaulingZoneChange,
        handleFormAddressChange,
        handleFormRegionChange,
        setCurrentSiteHaulingLineItem,
        setFormIsCustomDriverMemo,
        handleRateToggleChange,
        handleIsCustomPoNumberChange,
        handleIsCustomerJobNumberCustomChange,
        handleIsMaterialTaxableChange,
        currentSiteHaulingLineItem,
        formQuote,
        formSite,
        formEquipmentType,
        formRate,
        formQuantity,
        formIsBilledTo,
        formBilledTo,
        formIsMaterialOnly,
        formUnitOfMeasure,
        formIsActive,
        formMaterial,
        formMaterialPrice,
        formPriceCode,
        formCost,
        formTotal,
        formNewHaulingLineItemRate,
        formNewHaulingLineItemDriverRate,
        formNewHaulingLineItemBrokerRate,
        formNewHaulingLineItemMinimumFlatRate,
        formNewHaulingLineItemDriverMinimumFlatRate,
        formNewHaulingLineItemBrokerMinimumFlatRate,
        formNewHaulingLineItemZone,
        formLineItemMemo,
        formDriverMemo,
        formCustomerJobNumber,
        formPoNumber,
        formRateToggle,
        formIsCustomPoNumber,
        formIsCustomerJobNumberCustom,
        formIsCustomDriverMemo,
        formQuotePoNumber,
        formQuoteCustomerJobNumber,
        formSiteName,
        formAddressLine1,
        formAddressLine2,
        formCity,
        formState,
        formZipCode,
        formCountry,
        formDirections,
        formRegion,
        regions,
        formIsMaterialTaxable,
        validateField,
        isFobQuote,
        initValues,
        getCurrentRate,
        isNewMaterialLineItem,
    };
}
